import { LineAnnotation, PointAnnotation } from "@shared/types";
import { ResolutionString, VisiblePriceRange } from "charting_library";
import { dateToMoment } from "@shared/utils/date";
import moment from "moment-timezone";
import { TIME_RANGE_START } from "@shared/common/constants";
import { getFormattedChartXValue } from "@shared/utils/charts";

export const getChartStartRange = (interval: ResolutionString) => {
  let date = new Date();
  let years = 2;
  if (interval.includes("M")) years = 3;
  date.setFullYear(date.getFullYear() - years);
  return Math.floor(date.getTime() / 1000);
};

export const getNewPriceRange = (
  minPrice: number,
  maxPrice: number,
  initialPriceScale: VisiblePriceRange
) => {
  const from = initialPriceScale.from;
  const to = initialPriceScale.to;
  if (minPrice < from || maxPrice > to) {
    const extra = (Math.max(to, maxPrice) - Math.min(from, minPrice)) * 0.1;
    return {
      from: Math.min(from, minPrice - extra),
      to: Math.max(to, maxPrice + extra),
    };
  } else {
    return initialPriceScale;
  }
};

export const getStartResolution = (days: number) => {
  if (days <= 3) return "5";
  else if (days <= 7) return "15";
  else if (days <= 30) return "60";
  else if (days <= 90) return "240";
  else return "1d";
};

export const getResolutionByTimeFrame = (timeFrame: {
  from: number;
  to: number;
}) => {
  const startMoment = dateToMoment(timeFrame.from, true);
  const endMoment = dateToMoment(timeFrame.to, true);
  const days = endMoment.diff(startMoment, "days");
  return getStartResolution(days);
};

export const getStartTimeframe = (startTimestamp?: number) => {
  let text: string;
  let resolution: string;
  const startMoment = dateToMoment(startTimestamp, true);
  const endMoment = moment();
  let days = endMoment.diff(startMoment, "days");
  if (days === 0) {
    let hours = endMoment.diff(startMoment, "hours");
    const extraHours = Math.ceil(hours * 0.05);
    hours = Math.max(1, hours + extraHours);
    text = hours === 1 ? "60" : `${hours}h`;
    resolution = "5";
  } else {
    const extraDays = Math.ceil(days * 0.075);
    days = Math.max(1, days + extraDays);
    text = days === 1 ? "24h" : `${days}d`;
    resolution = getStartResolution(days);
  }

  return {
    text,
    resolution: resolution as ResolutionString,
    title: "🏁",
    description: TIME_RANGE_START,
  };
};

export const filteredAnnotationsByType = <
  T extends LineAnnotation | PointAnnotation
>(
  annotations: (LineAnnotation | PointAnnotation)[] | undefined,
  type: "line" | "point"
) => {
  return annotations?.filter((annotation) => annotation?.type === type) as T[];
};

export const isCloseNumber = (
  original: number,
  number: number,
  percent: number
) => {
  const absNumber = Math.abs(number);
  const absOriginal = Math.abs(original);
  const extra = absOriginal * percent;
  return absNumber < absOriginal + extra && absNumber > absOriginal - extra;
};

export const getEmptyGraphData = (seconds: number) => {
  const timestamp1 = Date.now() - seconds * 1000;
  const timestamp2 = Date.now();
  return [
    {
      y: 0,
      rawX: timestamp1,
      x: getFormattedChartXValue(timestamp1, false, false),
    },
    {
      y: 0,
      rawX: timestamp2,
      x: getFormattedChartXValue(timestamp2, false, false),
    },
  ];
};
